import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { FolderOpen, Plus, ExternalLink, Trash2, Edit2, X, Check, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react'
import { useDossiers, useCreateDossier, useUpdateDossier, useDeleteDossier, useGscProperties, useGscStatus } from '@/hooks/useApi'
import type { Dossier, DossierCreate } from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'

const EMPTY_FORM: DossierCreate = {
  name: '',
  url: '',
  client_name: '',
  gsc_property: '',
  notes: '',
  baseline_date: null,
  business_context: null,
}

const BUSINESS_TYPES = ['eenmanszaak', 'kmo', 'webshop', 'overig']

/**
 * A January-1st baseline is what you get when the field was left alone, not a
 * date anyone chose. Showing "Gestart: 1/1/2026" on every card spends a line
 * on nothing; a real start date still shows.
 */
function isPlaceholderBaseline(date: string): boolean {
  const d = new Date(date)
  return d.getMonth() === 0 && d.getDate() === 1
}

interface DossierFormProps {
  value: DossierCreate
  onChange: (v: DossierCreate) => void
}

function DossierForm({ value, onChange }: DossierFormProps) {
  const f = (field: keyof DossierCreate) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    onChange({ ...value, [field]: e.target.value || null })

  const { data: gscProperties } = useGscProperties()

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <label className="mb-1 block text-xs font-medium">Naam *</label>
        <Input placeholder="Smederij De Bruyn" value={value.name} onChange={f('name')} />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium">Website URL *</label>
        <Input placeholder="https://smederijdebruyn.be" value={value.url ?? ''} onChange={f('url')} />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium">Klantnaam</label>
        <Input placeholder="Jan De Bruyn" value={value.client_name ?? ''} onChange={f('client_name')} />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium">
          GSC Property
          <span className="ml-1 font-normal text-muted-foreground">(kies uit lijst of typ handmatig)</span>
        </label>
        {gscProperties && gscProperties.length > 0 ? (
          <Select
            value={value.gsc_property ?? ''}
            onChange={e => onChange({ ...value, gsc_property: e.target.value || null })}
          >
            <option value="">— Kies een GSC property —</option>
            {gscProperties.map(p => (
              <option key={p.siteUrl} value={p.siteUrl}>{p.siteUrl}</option>
            ))}
          </Select>
        ) : (
          <Input
            placeholder="sc-domain:example.be of https://example.be/"
            value={value.gsc_property ?? ''}
            onChange={f('gsc_property')}
          />
        )}
        <p className="mt-1 text-xs text-muted-foreground">
          Domain properties beginnen met <code className="bg-muted px-1 rounded">sc-domain:</code>
        </p>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium">Startdatum samenwerking</label>
        <Input type="date" value={value.baseline_date ?? ''} onChange={f('baseline_date')} />
      </div>
      <div className="sm:col-span-2">
        <label className="mb-1 block text-xs font-medium">Notities</label>
        <textarea
          className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          rows={2}
          placeholder="Doelstellingen, afspraken, context..."
          value={value.notes ?? ''}
          onChange={f('notes')}
        />
      </div>

      <div className="sm:col-span-2 border-t pt-3 mt-1">
        <p className="mb-2 text-xs font-medium text-muted-foreground">
          Bedrijfscontext <span className="font-normal">(gebruikt door de content-audit — voorkomt generiek advies)</span>
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium">Bedrijfstype</label>
            <Select
              value={value.business_context?.business_type ?? ''}
              onChange={e => onChange({
                ...value,
                business_context: {
                  business_type: e.target.value || null,
                  service_area: value.business_context?.service_area ?? [],
                  language: value.business_context?.language ?? null,
                  word_range: value.business_context?.word_range ?? null,
                },
              })}
            >
              <option value="">— Kies —</option>
              {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">Taal</label>
            <Select
              value={value.business_context?.language ?? ''}
              onChange={e => onChange({
                ...value,
                business_context: {
                  business_type: value.business_context?.business_type ?? null,
                  service_area: value.business_context?.service_area ?? [],
                  language: e.target.value || null,
                  word_range: value.business_context?.word_range ?? null,
                },
              })}
            >
              <option value="">— Kies —</option>
              <option value="nl">Nederlands</option>
              <option value="fr">Frans</option>
              <option value="en">Engels</option>
              <option value="de">Duits</option>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">
              Werkgebied <span className="font-normal text-muted-foreground">(gemeenten, komma-gescheiden)</span>
            </label>
            <Input
              placeholder="Erpe-Mere, Aalst, Ninove"
              value={value.business_context?.service_area?.join(', ') ?? ''}
              onChange={e => onChange({
                ...value,
                business_context: {
                  business_type: value.business_context?.business_type ?? null,
                  service_area: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                  language: value.business_context?.language ?? null,
                  word_range: value.business_context?.word_range ?? null,
                },
              })}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">
              Realistisch woordbereik <span className="font-normal text-muted-foreground">(dienstpagina)</span>
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                placeholder="min"
                value={value.business_context?.word_range?.[0] ?? ''}
                onChange={e => {
                  const min = e.target.value === '' ? null : Number(e.target.value)
                  const max = value.business_context?.word_range?.[1] ?? null
                  onChange({
                    ...value,
                    business_context: {
                      business_type: value.business_context?.business_type ?? null,
                      service_area: value.business_context?.service_area ?? [],
                      language: value.business_context?.language ?? null,
                      word_range: (min !== null && max !== null ? [min, max] : null) as [number, number] | null,
                    },
                  })
                }}
              />
              <span className="text-muted-foreground">–</span>
              <Input
                type="number"
                min={0}
                placeholder="max"
                value={value.business_context?.word_range?.[1] ?? ''}
                onChange={e => {
                  const max = e.target.value === '' ? null : Number(e.target.value)
                  const min = value.business_context?.word_range?.[0] ?? null
                  onChange({
                    ...value,
                    business_context: {
                      business_type: value.business_context?.business_type ?? null,
                      service_area: value.business_context?.service_area ?? [],
                      language: value.business_context?.language ?? null,
                      word_range: (min !== null && max !== null ? [min, max] : null) as [number, number] | null,
                    },
                  })
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Dossiers() {
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState<DossierCreate>({ ...EMPTY_FORM })
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState<DossierCreate>({ ...EMPTY_FORM })

  const { data: dossiers, isLoading } = useDossiers()
  const { data: gscStatus } = useGscStatus()
  const createDossier = useCreateDossier()
  const updateDossier = useUpdateDossier()
  const deleteDossier = useDeleteDossier()

  const handleCreate = async () => {
    if (!createForm.name.trim() || !createForm.url.trim()) {
      toast.error('Naam en URL zijn verplicht')
      return
    }
    try {
      await createDossier.mutateAsync(createForm)
      setShowCreate(false)
      setCreateForm({ ...EMPTY_FORM })
      toast.success(`Dossier '${createForm.name}' aangemaakt`)
    } catch (err) {
      toast.error('Aanmaken mislukt', { description: err instanceof Error ? err.message : 'Onbekende fout' })
    }
  }

  const handleUpdate = async (id: number) => {
    try {
      await updateDossier.mutateAsync({ id, data: editForm })
      setEditingId(null)
      toast.success('Dossier bijgewerkt')
    } catch (err) {
      toast.error('Bijwerken mislukt', { description: err instanceof Error ? err.message : 'Onbekende fout' })
    }
  }

  const handleDelete = (dossier: Dossier) => {
    toast(`Dossier '${dossier.name}' verwijderen?`, {
      action: {
        label: 'Verwijder',
        onClick: async () => {
          try {
            await deleteDossier.mutateAsync(dossier.id)
            toast.success('Dossier verwijderd')
          } catch (err) {
            toast.error('Verwijderen mislukt')
          }
        },
      },
    })
  }

  const startEdit = (dossier: Dossier) => {
    setEditingId(dossier.id)
    setEditForm({
      name: dossier.name,
      url: dossier.url,
      client_name: dossier.client_name,
      gsc_property: dossier.gsc_property,
      notes: dossier.notes,
      baseline_date: dossier.baseline_date,
      business_context: dossier.business_context,
    })
  }

  if (isLoading) return <div className="flex justify-center py-16"><Spinner size="lg" /></div>

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dossiers</h1>
          <p className="mt-1 text-muted-foreground">SEO-historiek en evolutie per website/klant.</p>
        </div>
        <Button onClick={() => setShowCreate(true)} disabled={showCreate}>
          <Plus className="mr-2 h-4 w-4" /> Nieuw dossier
        </Button>
      </div>

      {/* Create form */}
      {showCreate && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Nieuw dossier</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <DossierForm value={createForm} onChange={setCreateForm} />
            <div className="flex gap-2">
              <Button onClick={handleCreate} disabled={createDossier.isPending}>
                {createDossier.isPending ? <Spinner size="sm" className="mr-2" /> : null}
                Aanmaken
              </Button>
              <Button variant="outline" onClick={() => { setShowCreate(false); setCreateForm({ ...EMPTY_FORM }) }}>
                Annuleer
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dossier list */}
      {!dossiers?.length ? (
        <Card>
          <CardContent className="py-16 text-center">
            <FolderOpen className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">Nog geen dossiers. Maak er een aan om te beginnen.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {dossiers.map((d) => (
            <Card key={d.id} className={!d.active ? 'opacity-60' : ''}>
              <CardContent className="p-5 space-y-3">
                {editingId === d.id ? (
                  <>
                    <DossierForm value={editForm} onChange={setEditForm} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleUpdate(d.id)} disabled={updateDossier.isPending}>
                        <Check className="h-4 w-4 mr-1" /> Opslaan
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                        <X className="h-4 w-4 mr-1" /> Annuleer
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Header: naam + score badge */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{d.name}</p>
                        {d.client_name && (
                          <p className="text-xs text-muted-foreground">{d.client_name}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {/* SEO score */}
                        {d.last_avg_seo_score !== null && (
                          <div className="text-center">
                            <div className="text-xs text-muted-foreground leading-none">SEO</div>
                            {(() => {
                              const s = Math.min(100, Math.max(0, d.last_avg_seo_score!))
                              return (
                                <span className={`text-lg font-bold tabular-nums leading-tight ${
                                  s >= 75 ? 'text-green-600 dark:text-green-300' : s >= 50 ? 'text-orange-500' : 'text-red-500'
                                }`}>{s}</span>
                              )
                            })()}
                          </div>
                        )}
                        {/* Security indicator */}
                        {d.last_security_critical !== null && (
                          <div className="text-center">
                            <div className="text-xs text-muted-foreground leading-none">Sec.</div>
                            <span className={`text-sm font-bold leading-tight ${
                              d.last_security_critical > 0 ? 'text-red-600 dark:text-red-300' :
                              d.last_security_high && d.last_security_high > 0 ? 'text-orange-500' : 'text-green-600 dark:text-green-300'
                            }`}>
                              {d.last_security_critical > 0
                                ? `${d.last_security_critical}C`
                                : d.last_security_high && d.last_security_high > 0
                                ? `${d.last_security_high}H`
                                : '✓'}
                            </span>
                          </div>
                        )}
                        {!d.active && <Badge variant="outline" className="text-xs">Inactief</Badge>}
                        {d.gsc_property && gscStatus && !gscStatus.configured && (
                          <Badge variant="outline" className="text-xs border-red-300 text-red-600 dark:text-red-400">
                            GSC niet verbonden
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* GSC metrics + trend */}
                    {(d.last_gsc_clicks !== null || d.last_gsc_position !== null) && (
                      <div className="flex items-center gap-3 text-xs">
                        {d.last_gsc_clicks !== null && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <strong className="text-foreground">{d.last_gsc_clicks.toLocaleString('nl')}</strong> clicks
                            {d.prev_gsc_clicks !== null && (() => {
                              const diff = d.last_gsc_clicks! - d.prev_gsc_clicks!
                              if (diff > 0) return <span className="text-green-600 dark:text-green-300 flex items-center gap-0.5"><TrendingUp className="h-3 w-3" />+{diff}</span>
                              if (diff < 0) return <span className="text-red-500 flex items-center gap-0.5"><TrendingDown className="h-3 w-3" />{diff}</span>
                              return null
                            })()}
                          </span>
                        )}
                        {d.last_gsc_position !== null && (
                          <span className="text-muted-foreground">
                            pos. <strong className={`${
                              d.last_gsc_position <= 10 ? 'text-green-600 dark:text-green-300' :
                              d.last_gsc_position <= 20 ? 'text-orange-500' : 'text-red-500'
                            }`}>#{d.last_gsc_position.toFixed(1)}</strong>
                          </span>
                        )}
                        {d.gsc_property_shared_with?.length > 0 && (
                          <span
                            className="flex items-center gap-1 text-amber-600 dark:text-amber-300"
                            title={`Deze cijfers komen uit dezelfde GSC-property (${d.gsc_property}) als: ${d.gsc_property_shared_with.join(', ')}. Ze zijn dus niet uniek voor dit dossier.`}
                          >
                            <AlertTriangle className="h-3 w-3" />
                            gedeelde property
                          </span>
                        )}
                      </div>
                    )}

                    <a
                      href={d.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-primary hover:underline truncate"
                    >
                      {d.url} <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>

                    {d.notes && (
                      <p className="text-xs text-muted-foreground line-clamp-2">{d.notes}</p>
                    )}

                    {d.baseline_date && !isPlaceholderBaseline(d.baseline_date) && (
                      <p className="text-xs text-muted-foreground">
                        Gestart: {new Date(d.baseline_date).toLocaleDateString('nl-BE')}
                      </p>
                    )}

                    <div className="flex items-center gap-2 pt-1">
                      <Link to={`/dossiers/${d.id}`} className="flex-1">
                        <Button size="sm" className="w-full">
                          <FolderOpen className="h-4 w-4 mr-1.5" /> Open dossier
                        </Button>
                      </Link>
                      <button
                        onClick={() => startEdit(d)}
                        className="rounded p-1.5 text-muted-foreground hover:bg-muted transition-colors"
                        title="Bewerken"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(d)}
                        className="rounded p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                        title="Verwijderen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
